<template>
  <div class="card">
    <div class="card-header">
      <h3 class="card-title">Batch Create Valuations</h3>
    </div>

    <div class="card-body">
      <div class="row" style="margin-bottom: 0.75em">
        <div class="col-md-12">
          <file-upload
            title="Batch Create Valuations"
            style="margin-right: 2px"
            accept=".xlsx"
            @change="batchValuations"
          >
            <i class="fa fa-cloud-upload" aria-hidden="true"></i>
          </file-upload>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <p>
            Upload an Excel spreadsheet (use
            <a href="/templates/batch_valuations_template.xlsx">
              this template</a
            >) to create batches of valuations. The file can have errors, but
            those records will not be processed.
          </p>
        </div>
      </div>

      <div class="row" v-if="recs.length > 0">
        <div class="col-md-12">
          <label style="font-weight: bold">Records</label>
          <div class="table-responsive">
            <table
              class="table table-sm table-striped text-nowrap table-bordered"
              style="width: auto"
            >
              <thead>
                <tr>
                  <th scope="col" class="text-center">File Row<br />Number</th>
                  <th scope="col">Template<br />Type</th>
                  <th scope="col">Customer<br />Name</th>
                  <th scope="col">Address</th>
                  <th scope="col">Street<br />Number</th>
                  <th scope="col">Street<br />Name</th>
                  <th scope="col">Unit<br />Number</th>
                  <th scope="col">Project</th>
                  <th scope="col">Postal<br />Code</th>
                  <th scope="col" class="text-center">Postal<br />Sector</th>
                  <th scope="col" class="text-center">Postal<br />District</th>
                  <th scope="col">File Reference<br />Number</th>
                  <th scope="col">Request Date</th>
                  <th scope="col">Due Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="rec in recs">
                  <td class="text-center align-middle">{{ rec.ndx + 2 }}</td>
                  <td class="align-middle">
                    {{ rec.pri_report_template_type }}
                  </td>
                  <td class="align-middle">{{ rec.requestor_name }}</td>
                  <td class="align-middle">{{ rec.address }}</td>
                  <td class="align-middle">{{ rec.house_no }}</td>
                  <td class="align-middle">{{ rec.street_name }}</td>
                  <td class="align-middle">{{ rec.unit_no }}</td>
                  <td class="align-middle">{{ rec.project }}</td>
                  <td class="align-middle">{{ rec.postal_code }}</td>
                  <td class="text-center align-middle">
                    {{ rec.postal_sector }}
                  </td>
                  <td class="text-center align-middle">
                    {{ rec.postal_district }}
                  </td>
                  <td class="align-middle">{{ rec.file_ref_num }}</td>
                  <td class="align-middle">
                    {{ rec.request_dt }}
                  </td>
                  <td class="align-middle">{{ rec.due_dt }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="row" v-if="err_recs.length > 0">
        <div class="col-md-12">
          <label style="font-weight: bold">Errors</label>
          <p>
            The uploaded records have errors. You can still create valuations
            for those records which do not have errors. The errors will
            automatically be downloaded by the browser.
          </p>
          <div class="table-responsive">
            <table
              class="table table-sm table-striped text-nowrap table-bordered"
              style="width: auto"
            >
              <thead>
                <tr>
                  <th scope="col" class="text-center">File Row<br />Number</th>
                  <th scope="col">Error Reason</th>
                  <th scope="col">Template<br />Type</th>
                  <th scope="col">Customer<br />Name</th>
                  <th scope="col">Street<br />Number</th>
                  <th scope="col">Street<br />Name</th>
                  <th scope="col">Unit Number</th>
                  <th scope="col">Project</th>
                  <th scope="col">File Reference<br />Number</th>
                  <th scope="col">Request Date</th>
                  <th scope="col">Due Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="err_rec in err_recs">
                  <td class="text-center align-middle">
                    {{ err_rec.ndx + 2 }}
                  </td>
                  <td class="align-middle font-weight-bold">
                    {{ err_rec.err_reason }}
                  </td>
                  <td class="align-middle">
                    {{ err_rec.pri_report_template_type }}
                  </td>
                  <td class="align-middle">{{ err_rec.requestor_name }}</td>
                  <td class="align-middle">{{ err_rec.house_no }}</td>
                  <td class="align-middle">{{ err_rec.street_name }}</td>
                  <td class="align-middle">{{ err_rec.unit_no }}</td>
                  <td class="align-middle">{{ err_rec.project }}</td>
                  <td class="align-middle">{{ err_rec.file_ref_num }}</td>
                  <td class="align-middle">
                    {{ err_rec.request_dt }}
                  </td>
                  <td class="align-middle">{{ err_rec.due_dt }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="row" style="margin-top: 0.75em" v-if="recs.length > 0">
        <div class="col-md-12">
          <button
            type="submit"
            class="btn btn-primary pull-right"
            @click="createVals"
          >
            Batch Create Valuations
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script type="text/javascript">
import Swal from "sweetalert2";
import Datepicker from "vuejs-datepicker";
import FileUpload from "../../components/FileUpload";
import XLSX from "xlsx";
import { mapGetters, mapMutations } from "vuex";
import moment from "moment";

export default {
  components: {
    Datepicker,
    "file-upload": FileUpload,
  },

  // take the spinner and notify from App.vue
  inject: [
    "showSpinner",
    "hideSpinner",
    "notifyError",
    "notifyInfo",
    "notifyWarn",
  ],

  data() {
    return {
      // records that can be matched
      recs: [],

      // records that cannot be matched
      err_recs: [],
    };
  },

  computed: mapGetters(["xlsx"]),

  methods: {
    ...mapMutations(["storeXlsx"]),

    /**
     * store xlsx data in app state
     */
    async batchValuations(files) {
      this.storeXlsx(await this.loadXLSX(files[0]));

      if (this.xlsx.length > 0) {
        await this.checkFile();
      } else {
        this.notifyWarn("The file has no records");
      }
    },

    /**
     * load xlsx data from file
     */
    loadXLSX(file) {
      return new Promise((rslv, rjct) => {
        const reader = new FileReader();

        // onload handler
        reader.onload = (e) => {
          // pre-process data
          let binary = "";
          let bytes = new Uint8Array(e.target.result);
          let length = bytes.byteLength;

          for (let i = 0; i < length; i++) {
            binary += String.fromCharCode(bytes[i]);
          }

          let wb = XLSX.read(binary, { type: "binary" });

          /* grab first sheet */
          let wsname = wb.SheetNames[0];
          let ws = wb.Sheets[wsname];

          return rslv(XLSX.utils.sheet_to_json(ws));
        };

        // on error handler
        reader.onerror = (e) => {
          reader.abort();
          return rjct(e);
        };

        // start file read
        reader.readAsArrayBuffer(file);
      });
    },

    /**
     * do a quick check of the xls data
     * only first record is checked
     */
    checkFields(fields) {
      let fld_cnts = {};

      for (let rec of this.xlsx) {
        for (let field of fields) {
          if (!(field in rec)) {
            fld_cnts[field] = fld_cnts[field] || 0;
            fld_cnts[field]++;
          }
        }
      }

      for (let field of fields) {
        if (fld_cnts[field] && fld_cnts[field] >= this.xlsx.length) {
          return false;
        }
      }

      return true;
    },

    /**
     * check if the file validates
     */
    checkFile() {
      if (
        this.checkFields([
          "pri_report_template_type",
          "requestor_name",
          "house_no",
          "street_name",
        ])
      ) {
        return this.checkApi();
      } else {
        this.notifyError("The file has an incorrect format");
        this.storeXlsx([]);
      }
    },

    /**
     * api call to check the file
     */
    async checkApi() {
      this.showSpinner();

      try {
        let res = await this.$http.post(
          this.rootUrl + "valuation/batch/check",
          { records: this.xlsx }
        );

        this.refreshToken(res);

        this.recs = res.data.recs;
        this.err_recs = res.data.err_recs;
      } catch (error) {
        if (error.response) {
          this.notifyError(error.response.data.message);
        } else if (error.request) {
          this.notifyError("No response from Realvalues API");
        } else {
          this.notifyError(error.message);
        }
      }

      this.hideSpinner();
    },

    async createVals() {
      let rslt = await Swal({
        title: "Do you want to create these valuations?",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Create Valuations",
        cancelButtonText: "Cancel",
      });

      // user cancelled
      if (!rslt.value) return;

      if (this.recs.length === 0) {
        return this.notifyError("The file has no data");
      }

      this.showSpinner();

      try {
        let res = await this.$http.post(
          this.rootUrl + "valuation/batch/create",
          { records: this.recs }
        );

        this.refreshToken(res);

        if (this.err_recs.length > 0) {
          let file_name =
            "batch_vals_errors_" + `${moment().format("YYYYMMDDHHMMss")}.xlsx`;

          this.notifyInfo(`Downloading errors to ${file_name}`);
          this.downloadErrors(file_name);
        }

        // delete all data
        this.recs = [];
        this.err_recs = [];
        this.storeXlsx([]);

        this.$router.push(`/list-valuations`);
      } catch (error) {
        if (error.response) {
          this.notifyError(error.response.data.message);
        } else if (error.request) {
          this.notifyError("No response from Realvalues API");
        } else {
          this.notifyError(error.message);
        }
      }

      this.hideSpinner();
    },

    /**
     * download of xlsx data is synchronous
     */
    downloadErrors(file_name) {
      const ws = XLSX.utils.json_to_sheet(this.err_recs, {
        header: [
          "pri_report_template_type",
          "requestor_name",
          "house_no",
          "street_name",
          "unit_no",
          "project",
          "file_ref_num",
          "request_dt",
          "due_dt",
          "err_reason",
        ],
      });

      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, "batch_vals_errors");
      XLSX.writeFile(wb, file_name);
    },
  },

  async mounted() {
    if (this.xlsx.length > 0) {
      await this.checkFile();
    } else {
      if (!this.$route.params.no_file) {
        this.notifyWarn("The file has no records");
      }
    }
  },
};
</script>